import React, { useState } from "react";
import { Card, Button, Badge, Modal, Form, Spinner } from "react-bootstrap";
import Swal from "sweetalert2";
import {
  useFetchAnneeScolairesQuery,
  useCreateAnneeScolaireMutation,
  useDeleteAnneeScolaireMutation,
  useSetCurrentAnneeScolaireMutation,
} from "features/anneeScolaire/anneeScolaireSlice";

interface Props {
  onGoToArchives: (anneeLabel: string) => void;
}

const HistoryTab: React.FC<Props> = ({ onGoToArchives }) => {
  const { data: annees = [], isLoading } = useFetchAnneeScolairesQuery();
  const [createAnneeScolaire] = useCreateAnneeScolaireMutation();
  const [deleteAnneeScolaire] = useDeleteAnneeScolaireMutation();
  const [setCurrentAnneeScolaire] = useSetCurrentAnneeScolaireMutation();

  const [showCreate, setShowCreate] = useState(false);
  const [newLabel, setNewLabel] = useState("");

  const handleCreate = async () => {
    if (!newLabel || !/^\d{4}-\d{4}$/.test(newLabel)) {
      Swal.fire("Erreur", "Format requis : YYYY-YYYY (ex: 2024-2025)", "error");
      return;
    }
    try {
      await createAnneeScolaire({ label: newLabel, isCurrent: annees.length === 0 }).unwrap();
      setShowCreate(false);
      setNewLabel("");
      Swal.fire("Succès", "Année scolaire créée", "success");
    } catch (err: any) {
      Swal.fire("Erreur", err?.data?.message || "Erreur lors de la création", "error");
    }
  };

  const handleDelete = async (id: string, label: string) => {
    const result = await Swal.fire({
      title: `Supprimer ${label} ?`,
      text: "Cette action est irréversible. Les données archivées liées seront perdues.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Supprimer",
      cancelButtonText: "Annuler",
    });
    if (result.isConfirmed) {
      try {
        await deleteAnneeScolaire(id).unwrap();
        Swal.fire("Supprimé", "Année scolaire supprimée", "success");
      } catch (err: any) {
        Swal.fire("Erreur", err?.data?.message || "Erreur", "error");
      }
    }
  };

  const handleSetCurrent = async (id: string, label: string) => {
    const result = await Swal.fire({
      title: `Activer ${label} ?`,
      text: "Cette année deviendra l'année en cours.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Activer",
      cancelButtonText: "Annuler",
    });
    if (result.isConfirmed) {
      try {
        await setCurrentAnneeScolaire(id).unwrap();
        Swal.fire("Succès", `${label} est maintenant l'année en cours`, "success");
      } catch (err: any) {
        Swal.fire("Erreur", err?.data?.message || "Erreur", "error");
      }
    }
  };

  const sorted = [...annees].sort((a, b) => {
    if (a.isCurrent) return -1;
    if (b.isCurrent) return 1;
    return b.label.localeCompare(a.label);
  });

  return (
    <>
      <Card>
        <Card.Header className="d-flex align-items-center">
          <h5 className="card-title mb-0 flex-grow-1">
            <i className="ri-time-line me-2"></i>
            Historique des années scolaires
          </h5>
          <Button variant="primary" size="sm" onClick={() => setShowCreate(true)}>
            <i className="ri-add-line me-1"></i> Nouvelle année
          </Button>
        </Card.Header>
        <Card.Body>
          {isLoading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : annees.length === 0 ? (
            <div className="text-center py-5">
              <i className="ph ph-calendar-blank text-muted" style={{ fontSize: "3rem" }}></i>
              <p className="text-muted mt-3">Aucune année scolaire configurée</p>
              <Button variant="primary" onClick={() => setShowCreate(true)}>
                Créer la première année scolaire
              </Button>
            </div>
          ) : (
            <div className="timeline-container">
              {sorted.map((annee, idx) => (
                <div key={annee._id} className="d-flex mb-3">
                  {/* Timeline dot */}
                  <div className="d-flex flex-column align-items-center me-3" style={{ minWidth: 30 }}>
                    <div
                      className={`rounded-circle ${annee.isCurrent ? "bg-primary" : "bg-secondary"}`}
                      style={{ width: 12, height: 12, marginTop: 6 }}
                    />
                    {idx < sorted.length - 1 && (
                      <div className="flex-grow-1" style={{ width: 2, background: "var(--bs-border-color)" }} />
                    )}
                  </div>
                  {/* Content */}
                  <Card className={`flex-grow-1 ${annee.isCurrent ? "border-primary border-opacity-50" : ""}`}>
                    <Card.Body className="py-2 px-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h6 className="mb-1">
                            {annee.label}
                            {annee.isCurrent && (
                              <Badge bg="success" className="ms-2">En cours</Badge>
                            )}
                            {!annee.isCurrent && (
                              <Badge bg="secondary" className="ms-2">Archivée</Badge>
                            )}
                          </h6>
                          <small className="text-muted">
                            {annee.date_debut && (
                              <>Début : {new Date(annee.date_debut).toLocaleDateString("fr-FR")}</>
                            )}
                            {annee.date_fin && (
                              <> — Fin : {new Date(annee.date_fin).toLocaleDateString("fr-FR")}</>
                            )}
                          </small>
                        </div>
                        <div className="d-flex gap-1">
                          {!annee.isCurrent && (
                            <>
                              <Button
                                variant="soft-primary"
                                size="sm"
                                title="Voir les archives"
                                onClick={() => onGoToArchives(annee.label)}
                              >
                                <i className="ri-archive-line"></i>
                              </Button>
                              <Button
                                variant="soft-success"
                                size="sm"
                                title="Définir comme année en cours"
                                onClick={() => handleSetCurrent(annee._id, annee.label)}
                              >
                                <i className="ri-check-line"></i>
                              </Button>
                              <Button
                                variant="soft-danger"
                                size="sm"
                                title="Supprimer"
                                onClick={() => handleDelete(annee._id, annee.label)}
                              >
                                <i className="ri-delete-bin-line"></i>
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Modal Création */}
      <Modal show={showCreate} onHide={() => setShowCreate(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Nouvelle année scolaire</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Label (ex: 2024-2025)</Form.Label>
            <Form.Control
              type="text"
              placeholder="YYYY-YYYY"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreate(false)}>Annuler</Button>
          <Button variant="primary" onClick={handleCreate}>Créer</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default HistoryTab;
